import { z } from 'zod';
import { IsoDateTimeSchema } from './common.js';
import { DeviceCommandTypeSchema } from './device.js';
/**
 * Command Operations and Reliability Dashboard -- organisation-wide
 * aggregation and paginated records over device_commands + batch data.
 *
 * Metric definitions, the success-rate formula, and time-boundary rules
 * are documented in the task's approved implementation plan (see PR
 * description for the full writeup); this file is the wire contract
 * that implements them.
 */
export const CommandOperationsTimeRangeSchema = z.enum(['24h', '7d', '30d']);
/** Full wire status enum -- matches DeviceCommandStatusSchema / DeviceCommandHistoryItemSchema. */
export const CommandOperationsStatusSchema = z.enum([
    'pending',
    'sent',
    'acknowledged',
    'executed',
    'failed',
    'expired',
]);
export const CommandOperationsOriginSchema = z.enum(['individual', 'batch']);
/**
 * Shared query-parameter shape for both the summary and records endpoints
 * -- kept identical so a given filter set always means the same thing in
 * both places (count-consistency requirement).
 */
export const CommandOperationsFilterQuerySchema = z.object({
    range: CommandOperationsTimeRangeSchema,
    status: CommandOperationsStatusSchema.optional(),
    type: DeviceCommandTypeSchema.optional(),
    deviceId: z.string().uuid().optional(),
    screenGroupId: z.string().uuid().optional(),
    origin: CommandOperationsOriginSchema.optional(),
});
/**
 * Live lifecycle counts (pending/sent/acknowledged/longWaiting) are
 * query-time snapshots, NOT scoped to the selected time range -- a
 * command created before the window can still be actively in flight.
 * Time-window counts (totalCreated/executed/failed/expired) ARE scoped
 * to [rangeStart, rangeEnd). Never mix the two groups without this
 * distinction being explicit in the UI (see dashboard plan §3).
 *
 * successRate is null when executed + failed + expired === 0 -- the
 * dashboard renders "No completed commands" instead of a percentage.
 */
export const CommandOperationsSummarySchema = z.object({
    rangeStart: IsoDateTimeSchema,
    rangeEnd: IsoDateTimeSchema,
    totalCreated: z.number().int().nonnegative(),
    pending: z.number().int().nonnegative(),
    sent: z.number().int().nonnegative(),
    acknowledged: z.number().int().nonnegative(),
    executed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    expired: z.number().int().nonnegative(),
    longWaiting: z.number().int().nonnegative(),
    successRate: z.number().min(0).max(1).nullable(),
});
/**
 * One row in the operational records table. Extends the fields already
 * established by DeviceCommandHistoryItemSchema with the org-wide
 * context (device identity, batch/group origin) that a single-device
 * history view doesn't need.
 */
export const CommandOperationsRecordSchema = z.object({
    id: z.string().uuid(),
    type: DeviceCommandTypeSchema,
    status: CommandOperationsStatusSchema,
    deviceId: z.string().uuid(),
    deviceCode: z.string().min(1),
    origin: CommandOperationsOriginSchema,
    /** Present only when origin === 'batch'. */
    batchId: z.string().uuid().nullable(),
    /** Immutable snapshot name, present only when origin === 'batch'. */
    screenGroupName: z.string().nullable(),
    isLongWaiting: z.boolean(),
    issuedByUserId: z.string().uuid().nullable(),
    issuedByDisplayName: z.string().nullable(),
    createdAt: IsoDateTimeSchema,
    sentAt: IsoDateTimeSchema.nullable(),
    acknowledgedAt: IsoDateTimeSchema.nullable(),
    expiresAt: IsoDateTimeSchema.nullable(),
    resultMessage: z.string().nullable(),
});
/** Opaque, base64url-encoded keyset cursor -- callers must never construct or inspect it. */
export const CommandOperationsRecordsResponseSchema = z.object({
    items: z.array(CommandOperationsRecordSchema),
    nextCursor: z.string().nullable(),
});
//# sourceMappingURL=command-operations.js.map